import { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../../../../utils/api';
import { collectCatalogTokens, filterMentionTokens, mentionQuery, splitMentions } from './logicChatMentions';

function speakerLabel(role) {
  return role === 'user' ? 'You' : 'Logic';
}

function readTokenUsage(usage) {
  if (!usage || typeof usage !== 'object') {
    return null;
  }

  const promptTokens = Number(usage.promptTokens ?? usage.PromptTokens ?? 0);
  const completionTokens = Number(usage.completionTokens ?? usage.CompletionTokens ?? 0);
  const totalTokens = Number(usage.totalTokens ?? usage.TotalTokens ?? 0);
  const total = totalTokens > 0 ? totalTokens : promptTokens + completionTokens;
  if (!Number.isFinite(total) || total <= 0) {
    return null;
  }

  return {
    promptTokens: Number.isFinite(promptTokens) ? promptTokens : 0,
    completionTokens: Number.isFinite(completionTokens) ? completionTokens : 0,
    totalTokens: total
  };
}

function formatTokenCount(value) {
  return Number(value || 0).toLocaleString();
}

async function writeClipboard(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Some browsers reject clipboard.writeText when the document is not focused.
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  textarea.remove();
  if (!copied) {
    throw new Error('Failed to copy chat.');
  }
}

function MentionText({ text, tokens, onUser }) {
  const segments = splitMentions(text, tokens);
  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type !== 'mention') {
          return <span key={`${segment.type}-${index}`}>{segment.text}</span>;
        }

        const isValue = segment.kind === 'value';
        return (
          <span
            key={`${segment.kind}-${index}`}
            title={isValue ? 'Value' : 'Feature'}
            style={{
              display: 'inline',
              padding: '0.05em 0.28em',
              borderRadius: '4px',
              border: onUser
                ? '1px solid rgba(255,255,255,0.55)'
                : '1px solid var(--border)',
              background: onUser
                ? (isValue ? 'rgba(255,255,255,0.18)' : 'transparent')
                : (isValue ? 'var(--accent-glow)' : 'var(--bg-card)'),
              color: onUser
                ? '#fff'
                : (isValue ? 'var(--accent)' : 'var(--text-primary)'),
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontSize: '0.92em',
              fontWeight: 600
            }}
          >
            {segment.text}
          </span>
        );
      })}
    </>
  );
}

export default function LogicChatPanel({ familyId, selections, features = [], onProposeApply, onAvailableChange }) {
  const [available, setAvailable] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [lastUsage, setLastUsage] = useState(null);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [cursor, setCursor] = useState(0);
  const endRef = useRef(null);
  const copiedResetRef = useRef(null);
  const inputRef = useRef(null);
  const catalogTokens = useMemo(() => collectCatalogTokens(features), [features]);
  const activeMention = mentionQuery(input, cursor);
  const mentionOptions = useMemo(
    () => (activeMention ? filterMentionTokens(catalogTokens, activeMention.query) : []),
    [activeMention, catalogTokens]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/logic-chat/status');
        const data = await res.json();
        if (!cancelled) {
          setAvailable(data?.llmConfigured === true);
        }
      } catch {
        if (!cancelled) {
          setAvailable(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    onAvailableChange?.(available === true);
  }, [available, onAvailableChange]);

  useEffect(() => {
    if (endRef.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  useEffect(() => {
    setMentionIndex(0);
  }, [activeMention?.start, activeMention?.query]);

  useEffect(() => () => {
    if (copiedResetRef.current) {
      clearTimeout(copiedResetRef.current);
    }
  }, []);

  const insertMention = (token) => {
    if (!activeMention || !token) {
      return;
    }

    const cursor = inputRef.current?.selectionStart ?? input.length;
    const next = `${input.slice(0, activeMention.start)}@${token.text} ${input.slice(cursor)}`;
    const nextCursor = activeMention.start + token.text.length + 2;
    setInput(next);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const nextHistory = messages
      .filter(message => message.role === 'user' || message.role === 'assistant')
      .map(message => ({
        role: message.role,
        content: message.content,
        apply: message.proposal?.apply || undefined
      }));

    setInput('');
    setError('');
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const url = familyId === 'global' ? '/api/logic-chat' : `/api/products/${familyId}/logic-chat`;
      const res = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          activeSelections: selections,
          history: nextHistory
        })
      });
      const data = await res.json();
      if (!res.ok && !data?.reply) {
        throw new Error(data?.message || data?.title || 'Logic chat request failed.');
      }

      if (data?.llmConfigured === false) {
        setAvailable(false);
        return;
      }

      const usage = readTokenUsage(data?.usage);
      if (usage) {
        setLastUsage(usage);
        setSessionTotal(total => total + usage.totalTokens);
      }

      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: data.reply || 'No reply was returned.',
          proposal: data.proposal || null,
          llmConfigured: data.llmConfigured
        }
      ]);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Logic chat request failed.');
    } finally {
      setLoading(false);
    }
  };

  const copyChat = async () => {
    if (messages.length === 0) return;

    const transcript = messages.map((message) => {
      const visible = splitMentions(message.content || '', catalogTokens)
        .map(segment => segment.text)
        .join('');
      const lines = [`${speakerLabel(message.role)}: ${visible}`];
      if (message.proposal?.apply) {
        const apply = Object.entries(message.proposal.apply)
          .map(([feature, value]) => `${feature}=${value}`)
          .join(', ');
        if (apply) {
          lines.push(`Suggested change: ${apply}`);
        }
      }
      return lines.join('\n');
    }).join('\n\n');

    try {
      await writeClipboard(transcript);
      setCopied(true);
      if (copiedResetRef.current) {
        clearTimeout(copiedResetRef.current);
      }
      copiedResetRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to copy chat.');
    }
  };

  const applyProposal = (proposal) => {
    if (!proposal?.apply) return;
    const applyEntries = Object.entries(proposal.apply);
    const [targetFeature, targetValue] = applyEntries[0] || ['', ''];
    onProposeApply({
      targetFeature,
      targetValue,
      removedSelections: proposal.removed || [],
      changedSelections: proposal.changedSelections || [],
      newState: proposal.previewNewState || {},
      familyId: familyId === 'global' ? null : familyId,
      message: proposal.message
    });
  };

  if (available !== true) {
    return null;
  }

  return (
    <div style={{
      width: '100%',
      height: '100%',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      background: 'var(--bg-card)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      minWidth: 0
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '0.75rem',
        padding: '0.75rem 1rem',
        background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border)',
        flex: '0 0 auto',
        flexWrap: 'wrap'
      }}>
        <h3 style={{
          fontSize: '0.9rem',
          fontWeight: 600,
          lineHeight: 1.3,
          margin: 0,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}>
          <i className="fa-solid fa-comments" style={{ color: 'var(--accent)' }}></i>
          Logic chat
          <span
            title="Beta"
            style={{
              fontSize: '0.55rem',
              fontWeight: 700,
              letterSpacing: '0.08em',
              lineHeight: 1,
              padding: '0.12rem 0.28rem',
              borderRadius: '3px',
              border: '1px solid var(--border)',
              background: 'var(--accent-glow)',
              color: 'var(--accent)'
            }}
          >
            beta
          </span>
        </h3>
        <button
          type="button"
          className="btn-secondary"
          onClick={copyChat}
          disabled={messages.length === 0}
          title="Copy chat"
          style={{
            padding: '0.25rem 0.5rem',
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.3rem',
            background: copied ? 'var(--success)' : 'transparent',
            color: copied ? '#fff' : 'var(--text-secondary)'
          }}
        >
          {copied
            ? <><i className="fa-solid fa-check"></i> Copied</>
            : <><i className="fa-regular fa-copy"></i> Copy chat</>}
        </button>
      </div>
      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.65rem',
        padding: '0.75rem 1rem'
      }}>
        {messages.length === 0 && (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', fontStyle: 'italic' }}>
            Ask what is available, what a feature does, or what happens if you add an option. Answers come from the rule engine; apply still needs your confirmation. Use @ when naming a feature or value.
          </p>
        )}
        {messages.map((message, index) => (
          <div
            key={`${message.role}-${index}`}
            style={{
              alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: message.role === 'user' ? 'flex-end' : 'flex-start'
            }}
          >
            <div style={{
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: 'var(--text-secondary)',
              marginBottom: '0.2rem',
              padding: '0 0.15rem'
            }}>
              {speakerLabel(message.role)}
            </div>
            <div style={{
              background: message.role === 'user' ? 'var(--accent)' : 'var(--bg-secondary)',
              color: message.role === 'user' ? '#fff' : 'var(--text-primary)',
              borderRadius: '8px',
              padding: '0.6rem 0.75rem',
              fontSize: '0.85rem',
              whiteSpace: 'pre-wrap',
              width: '100%'
            }}>
              <div>
                <MentionText
                  text={message.content}
                  tokens={catalogTokens}
                  onUser={message.role === 'user'}
                />
              </div>
              {message.proposal?.apply && (
                <div style={{
                  marginTop: '0.65rem',
                  paddingTop: '0.65rem',
                  borderTop: '1px solid var(--border)',
                  color: 'var(--text-primary)'
                }}>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>
                    Suggested change
                  </div>
                  {Object.entries(message.proposal.apply).map(([feature, value]) => (
                    <div key={feature}>
                      <MentionText text={feature} tokens={catalogTokens} />
                      {' = '}
                      <MentionText text={value} tokens={catalogTokens} />
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn-primary"
                    style={{ marginTop: '0.6rem', width: '100%' }}
                    onClick={() => applyProposal(message.proposal)}
                  >
                    Review and apply
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Querying the rule engine…</div>
        )}
        <div ref={endRef} />
      </div>
      {error && (
        <div style={{ color: 'var(--error)', fontSize: '0.8rem', padding: '0 1rem 0.5rem' }}>{error}</div>
      )}
      {lastUsage && (
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', padding: '0 1rem 0.5rem' }}>
          Last {formatTokenCount(lastUsage.totalTokens)}
          {' '}
          (in {formatTokenCount(lastUsage.promptTokens)} · out {formatTokenCount(lastUsage.completionTokens)})
          {' · Session '}
          {formatTokenCount(sessionTotal)}
        </div>
      )}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (mentionOptions.length > 0) {
            insertMention(mentionOptions[mentionIndex] || mentionOptions[0]);
            return;
          }
          sendMessage();
        }}
        style={{ display: 'flex', gap: '0.4rem', marginTop: 'auto', padding: '0.75rem 1rem', borderTop: '1px solid var(--border)', position: 'relative' }}
      >
        {mentionOptions.length > 0 && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: '4.5rem',
              bottom: '100%',
              marginBottom: '0.35rem',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
              maxHeight: '180px',
              overflowY: 'auto',
              zIndex: 5
            }}
          >
            {mentionOptions.map((token, index) => (
              <button
                key={`${token.kind}-${token.text}`}
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  insertMention(token);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  background: index === mentionIndex ? 'var(--bg-secondary)' : 'transparent',
                  color: 'var(--text-primary)',
                  padding: '0.4rem 0.65rem',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'
                }}
              >
                @{token.text}
                <span style={{ marginLeft: '0.4rem', color: 'var(--text-secondary)', fontFamily: 'inherit', fontSize: '0.7rem' }}>
                  {token.kind}
                </span>
              </button>
            ))}
          </div>
        )}
        <input
          ref={inputRef}
          className="input-field"
          value={input}
          onChange={(event) => {
            setInput(event.target.value);
            setCursor(event.target.selectionStart ?? event.target.value.length);
          }}
          onClick={(event) => setCursor(event.target.selectionStart ?? 0)}
          onKeyUp={(event) => setCursor(event.target.selectionStart ?? 0)}
          onKeyDown={(event) => {
            if (mentionOptions.length === 0) {
              return;
            }

            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setMentionIndex(index => (index + 1) % mentionOptions.length);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setMentionIndex(index => (index - 1 + mentionOptions.length) % mentionOptions.length);
            } else if (event.key === 'Escape') {
              event.preventDefault();
              setInput(input.replace(/@[A-Za-z0-9._-]*$/, ''));
            }
          }}
          placeholder="Ask the model… Use @ for features and values"
          disabled={loading}
          style={{ flex: 1, padding: '0.45rem 0.65rem', borderRadius: '4px', border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}
        />
        <button type="submit" className="btn-primary" disabled={loading || !input.trim()}>
          Send
        </button>
      </form>
    </div>
  );
}
